import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { PRODUCT_PARAMS } from '../../utils/queryParamsUtils';
import useGetProduct from '../../api/useGetProduct';
import useGetProductsByCategory from '../../api/useGetProductsByCategory';
import useUpdateProduct from '../../api/useUpdateProduct';
import { buildMinimalUpdatePayload } from '../../utils/productUpdateConverter';
import { ProductDetail } from '../../types/ProductTypes';
import TabMenu, { TabType } from '../../components/product/TabMenu';
import { addToProductHistory } from '../../utils/productHistory';
import GeneralInformationSection from '../../components/product/sections/GeneralInformationSection';
import OperationalHeatingSection from '../../components/product/sections/OperationalHeatingSection';
import RegulationsSection from '../../components/product/sections/RegulationsSection';
import USCGCompatibilitySection from '../../components/product/sections/USCGCompatibilitySection';
import PhysicalPropertiesSection from '../../components/product/sections/PhysicalPropertiesSection';
import RulesRegulationsSection from '../../components/product/sections/RulesRegulationsSection';
import ProceduresSection from '../../components/product/sections/ProceduresSection';
import OtherProceduresSection from '../../components/product/sections/OtherProceduresSection';
import ORCASection from '../../components/product/sections/ORCASection';
import CommentsSection from '../../components/product/sections/CommentsSection';
import ProductHeader from '../../components/product/ProductHeader';
import { ProductEditContextProvider, useProductEdit } from '../../context/ProductEditContext';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import {
    ProductPageContainer,
    LoadingContainer,
    LoadingSpinner,
    ErrorContainer,
    ErrorActions,
    RetryButton,
    BackButton,
    NotFoundContainer,
    DefaultProductLabel,
    ProductContent,
    ProductLayout,
    Sidebar,
    MainContent,
    ProductSections,
} from './ProductPage.components';
import CoatingCompatibilitiesSection from 'src/components/product/sections/CoatingCompatibilitiesSection';
import MaterialSafetyDataSheetsSection from 'src/components/product/sections/MaterialSafetyDataSheetsSection';
import SafetyInformationSection from 'src/components/product/sections/SafetyInformationSection';
import ExternalIdsSection from 'src/components/product/sections/ExternalIdsSection';

const ProductPageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <PageWrapper>
        <PageContainer>
            <ProductPageContainer>{children}</ProductPageContainer>
        </PageContainer>
    </PageWrapper>
);

const ProductPage: React.FC = () => {
    const navigate = useNavigate();
    const { queryParamUpdate, getQueryParamValueFromUrl, triggerQueryParamsUpdate } = useQueryParamsHandler();
    const [productId, setProductId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>(() => {
        // Initialize from URL, default to 'all' if not present or invalid
        const urlTab = new URLSearchParams(window.location.search).get(PRODUCT_PARAMS.TAB);
        const validTabs: TabType[] = [
            'all',
            'general',
            'operational',
            'regulations',
            'uscg',
            'coating',
            'safety',
            'cleaning',
            'other-procedures',
            'external',
            'orca',
        ];
        return validTabs.includes(urlTab as TabType) ? (urlTab as TabType) : 'all';
    });
    const { getProduct, isLoading, error, product } = useGetProduct(productId || '');
    const { products: relatedProducts } = useGetProductsByCategory(product?.categoryId);
    const { updateProduct } = useUpdateProduct();
    const { getPendingChanges, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges } = useProductEdit();
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const [isSaving, setIsSaving] = useState(false);

    // Initialize productId from URL on mount
    useEffect(() => {
        const initialProductId = getQueryParamValueFromUrl(PRODUCT_PARAMS.PRODUCT_ID);
        if (initialProductId) {
            setProductId(initialProductId);
        }
    }, [getQueryParamValueFromUrl]);

    // Handle productId changes from URL query parameters
    useEffect(() => {
        if (!queryParamUpdate?.queryParamTrigger) return;

        const productIdTrigger = queryParamUpdate.queryParamTrigger.find((qp) => qp.name === PRODUCT_PARAMS.PRODUCT_ID);
        if (productIdTrigger?.newValue) {
            setProductId(productIdTrigger.newValue);
        } else if (productIdTrigger?.clearParam) {
            setProductId(null);
        }

        // Handle tab parameter changes
        const tabTrigger = queryParamUpdate.queryParamTrigger.find((qp) => qp.name === PRODUCT_PARAMS.TAB);
        if (tabTrigger?.newValue) {
            const validTabs: TabType[] = [
                'all',
                'general',
                'operational',
                'regulations',
                'uscg',
                'coating',
                'safety',
                'cleaning',
                'other-procedures',
                'external',
                'orca',
            ];
            if (validTabs.includes(tabTrigger.newValue as TabType)) {
                setActiveTab(tabTrigger.newValue as TabType);
            }
        } else if (tabTrigger?.clearParam) {
            setActiveTab('all');
        }
    }, [queryParamUpdate]);

    // Update URL when tab changes
    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        // Update URL with new tab, but only if it's not 'all' (default)
        if (tab === 'all') {
            triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.TAB, clearParam: true }]);
        } else {
            triggerQueryParamsUpdate([{ name: PRODUCT_PARAMS.TAB, newValue: tab }]);
        }
    };

    // Fetch product when productId changes
    useEffect(() => {
        if (productId) {
            getProduct();
        }
    }, [productId]);

    // Track product visit in history
    useEffect(() => {
        if (product && product.id) {
            const productMetadata = {
                id: product.id,
                tradeName: product.tradeName,
                categoryName: product.category?.name || '',
                chartererName: product.charterer?.name || '',
                casNumber: '',
                marpolRegulation: '',
                isActive: product.isActive,
            };
            addToProductHistory(productMetadata);
        }
    }, [product]);

    const handleBackClick = () => {
        navigate('/products');
    };

    const handleSave = useCallback(async () => {
        if (!product) return;

        setIsSaving(true);
        try {
            // Get all pending changes from context (includes changes from all sections)
            const pendingChanges = getPendingChanges();

            // Build payload from pending changes
            // Convert synonyms from Synonym[] to string[] if needed
            const synonymsStrings = pendingChanges.synonyms?.map((syn: any) => (typeof syn === 'string' ? syn : syn.synonymName || syn));

            const payload = buildMinimalUpdatePayload(product, {
                tradeName: pendingChanges.tradeName,
                synonyms: synonymsStrings,
                casNumber: pendingChanges.externalIds?.find((id: any) => id.source === 'CAS')?.value,
                description: pendingChanges.comments?.find((c: any) => c.category === 4)?.text,
                warning: pendingChanges.comments?.find((c: any) => c.category === 5)?.text,
                chartererName: pendingChanges.charterer?.name,
                categoryName: pendingChanges.category?.name,
                // Include all non-description/warning comments (e.g., coating comments)
                additionalComments: pendingChanges.comments
                    ?.filter((c: any) => c.category !== 4 && c.category !== 5)
                    .map((c: any) => ({
                        id: c.id,
                        text: c.text,
                        category: c.category,
                        visibility: c.visibility,
                        lastUpdatedUtc: c.lastUpdatedUtc,
                    })),
                coatingCompatibilities: pendingChanges.coatingCompatibilities,
                procedures: pendingChanges.procedures,
                externalIds: pendingChanges.externalIds,
                stowageRules: pendingChanges.stowageRules,
                temperatureRequirements: pendingChanges.temperatureRequirements,
                regulations: pendingChanges.regulations,
                category: pendingChanges.category,
            });

            const response = await updateProduct(payload);
            if (response.success) {
                clearPendingChanges();
                // Refresh the product data from the server
                getProduct();
            } else {
                const errorMsg = response.errors?.join(', ') || 'Unknown error';
                alert(`Failed to update product: ${errorMsg}`);
            }
        } catch (err) {
            console.error('Save failed:', err);
            alert('Failed to save product');
        } finally {
            setIsSaving(false);
        }
    }, [product, getPendingChanges, updateProduct, clearPendingChanges, getProduct]);

    const handleCancel = useCallback(() => {
        clearPendingChanges();
    }, [clearPendingChanges]);

    const handleCopyProduct = useCallback(() => {
        if (!product || !canEdit || !isInEditMode) return;

        // Check if there are unsaved changes
        const pendingChanges = getPendingChanges();
        const hasPendingChanges = Object.keys(pendingChanges).length > 0;

        if (hasPendingChanges) {
            alert('Please save or discard your changes before copying the product.');
            return;
        }

        // Confirm the copy operation
        if (
            !confirm(
                'This will copy all details from the current product (except External IDs) into a new product. The new product will have the Trade Name set to the category name and will be marked as inactive. Continue?'
            )
        ) {
            return;
        }

        // Create changes object that represents the copied product
        const copiedChanges: Partial<ProductDetail> = {
            // Core fields - clear to indicate new product
            tradeName: product.category?.name || '',
            charterer: { id: 0, name: '', code: '', externalId: 0, lastUpdatedUtc: new Date().toISOString() },
            externalIds: [], // Clear external IDs as per requirement

            // Copy all existing data
            synonyms: product.synonyms,
            procedures: product.procedures,
            comments: product.comments,
            coatingCompatibilities: product.coatingCompatibilities,
            stowageRules: product.stowageRules,
            temperatureRequirements: product.temperatureRequirements,
            regulations: product.regulations,
            category: product.category,
            physicalData: product.physicalData,
            gasPhysicalData: product.gasPhysicalData,
            draegerTubes: product.draegerTubes,
            mainHazards: product.mainHazards,
            isNonCargo: product.isNonCargo,

            // New product defaults
            // Note: isActive and isApproved will be handled by backend when id=0
        };

        // Clear existing pending changes and set the copied data
        clearPendingChanges();
        // Use mergePendingChanges to apply all copied data at once
        mergePendingChanges(copiedChanges);

        // Mark form as having changes so save button is enabled
        setHasUnsavedChanges(true);

        // Scroll to top to show the form
        window.scrollTo({ top: 0, behavior: 'smooth' });

        alert(
            'Product copied successfully! The form now contains all details from the original product (except External IDs). The Trade Name has been set to the category name, and the Charterer has been cleared. Click Save to create the new product.'
        );
    }, [product, canEdit, isInEditMode, getPendingChanges, clearPendingChanges, mergePendingChanges, setHasUnsavedChanges]);

    if (isLoading) {
        return (
            <ProductPageShell>
                <LoadingContainer>
                    <LoadingSpinner />
                    <p>Loading product details...</p>
                </LoadingContainer>
            </ProductPageShell>
        );
    }

    if (error) {
        return (
            <ProductPageShell>
                <ErrorContainer>
                    <h2>Error Loading Product</h2>
                    <p>{error}</p>
                    <ErrorActions>
                        <RetryButton onClick={() => getProduct()}>Retry</RetryButton>
                        <BackButton onClick={handleBackClick}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to Products
                        </BackButton>
                    </ErrorActions>
                </ErrorContainer>
            </ProductPageShell>
        );
    }

    if (!product) {
        return (
            <ProductPageShell>
                <NotFoundContainer>
                    <h2>Product Not Found</h2>
                    <p>The requested product could not be found.</p>
                    <BackButton onClick={handleBackClick}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Back to Products
                    </BackButton>
                </NotFoundContainer>
            </ProductPageShell>
        );
    }

    const renderTemperatureRange = (range: any) => {
        if (!range) return 'N/A';
        const min = range.minValue?.value;
        const max = range.maxValue?.value;
        if (min && max) return `${min}°C - ${max}°C`;
        if (min) return `${min}°C+`;
        if (max) return `≤${max}°C`;
        return 'N/A';
    };

    // Check if this is a trade name product (not a category product)
    const isProductCategory = product.id === product.categoryId;
    const showCopyButton = !isProductCategory; // Only show for trade name products

    // Check if there are unsaved changes
    const pendingChanges = getPendingChanges();
    const hasUnsavedChanges = Object.keys(pendingChanges).length > 0;

    return (
        <ProductPageShell>
            <ProductHeader
                title={product.tradeName || product.category?.name || 'Unknown Product'}
                subtitle={product.category && !product.tradeName ? <DefaultProductLabel> [default product]</DefaultProductLabel> : undefined}
                isActive={product.isActive}
                product={product}
                onBackClick={handleBackClick}
                backButtonTitle="Back to Products"
                showPrintButton={true}
                showStatusBadge={true}
                showEditButtons={true}
                showCopyButton={showCopyButton}
                onSave={handleSave}
                onCancel={handleCancel}
                onCopy={handleCopyProduct}
                isSaving={isSaving}
                hasUnsavedChanges={hasUnsavedChanges}
            ></ProductHeader>

            <ProductContent>
                <TabMenu activeTab={activeTab} onTabChange={handleTabChange} />

                <ProductLayout>
                    <Sidebar>
                        <PhysicalPropertiesSection product={product} />
                        <RulesRegulationsSection product={product} />
                    </Sidebar>

                    <MainContent>
                        <ProductSections>
                            {(activeTab === 'all' || activeTab === 'general') && (
                                <GeneralInformationSection product={product} relatedProducts={relatedProducts} onProductUpdated={getProduct} />
                            )}
                            {(activeTab === 'all' || activeTab === 'operational') && (
                                <OperationalHeatingSection product={product} renderTemperatureRange={renderTemperatureRange} />
                            )}
                            {(activeTab === 'all' || activeTab === 'regulations') && <RegulationsSection product={product} />}
                            {(activeTab === 'all' || activeTab === 'uscg') && <USCGCompatibilitySection product={product} />}
                            {(activeTab === 'all' || activeTab === 'safety') && (
                                <SafetyInformationSection product={product} isProductCategory={!product.tradeName} />
                            )}
                            {(activeTab === 'all' || activeTab === 'coating') && (
                                <CoatingCompatibilitiesSection product={product} relatedProducts={relatedProducts} />
                            )}{' '}
                            {(activeTab === 'all' || activeTab === 'cleaning') && <ProceduresSection product={product} />}
                            {(activeTab === 'all' || activeTab === 'other-procedures') && <OtherProceduresSection product={product} />}
                            {activeTab === 'all' && <CommentsSection comments={product.comments} />}
                            {activeTab === 'all' && <MaterialSafetyDataSheetsSection msds={product.materialSafetyDataSheets} />}
                            {(activeTab === 'all' || activeTab === 'external') && <ExternalIdsSection product={product} />}
                            {(activeTab === 'all' || activeTab === 'orca') && <ORCASection product={product} />}
                        </ProductSections>
                    </MainContent>
                </ProductLayout>
            </ProductContent>
        </ProductPageShell>
    );
};

// Wrap with ProductEditContextProvider to enable save coordination between sections
const ProductPageWithContext: React.FC = () => (
    <ProductEditContextProvider>
        <ProductPage />
    </ProductEditContextProvider>
);

export default ProductPageWithContext;
